import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { apiRequest } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const RANGE_OPTIONS = [
  { key: '1h', label: 'Last Hour' },
  { key: 'today', label: 'Today' },
  { key: '1d', label: '24h' },
  { key: '3d', label: '3d' },
  { key: '7d', label: '7d' },
  { key: '14d', label: '14d' },
  { key: '30d', label: '30d' },
  { key: 'custom', label: 'Custom' },
];

// datetime-local input value → ISO; empty → null
const dtLocalToIso = (v) => (v ? new Date(v).toISOString() : null);

const fmtMs = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(2)}s` : `${Math.round(v)}ms`);

const AdminApiMetrics = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totalCalls, setTotalCalls] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [windowLabel, setWindowLabel] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('');
  const [endpointFilter, setEndpointFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const now = Date.now();
      let from = null;
      let to = null;
      if (range === 'custom') {
        from = dtLocalToIso(customFrom);
        to = dtLocalToIso(customTo);
        if (!from) { setLoading(false); return; }
      } else if (range === '1h') {
        from = new Date(now - 60 * 60 * 1000).toISOString();
      } else if (range === 'today') {
        const d = new Date(now);
        d.setHours(0, 0, 0, 0);
        from = d.toISOString();
      } else {
        const days = parseInt(range, 10) || 7;
        from = new Date(now - days * 24 * 60 * 60 * 1000).toISOString();
      }
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const resp = await apiRequest(`/api/metrics/api-usage?${params.toString()}`);
      if (resp.data?.success) {
        setRows(resp.data.rows || []);
        setTotalCalls(resp.data.totalCalls || 0);
        setTruncated(!!resp.data.truncated);
        const fmt = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        setWindowLabel(resp.data.to ? `${fmt(resp.data.from)} – ${fmt(resp.data.to)}` : `since ${fmt(resp.data.from)}`);
      } else {
        setError(resp.data?.error || 'Failed to load API metrics');
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load API metrics');
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const providers = useMemo(() => [...new Set(rows.map(r => r.provider))].sort(), [rows]);
  const users = useMemo(() => [...new Set(rows.map(r => r.userId || '-'))].sort(), [rows]);

  const filtered = useMemo(() => rows.filter(r =>
    (providerFilter === 'all' || r.provider === providerFilter) &&
    (!userFilter || (r.userId || '-') === userFilter) &&
    (!endpointFilter || r.endpoint.toLowerCase().includes(endpointFilter.toLowerCase()))
  ), [rows, providerFilter, userFilter, endpointFilter]);

  const providerSummary = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const b = map.get(r.provider) || { provider: r.provider, calls: 0, errors: 0 };
      b.calls += r.calls;
      b.errors += r.errors;
      map.set(r.provider, b);
    }
    return [...map.values()].sort((a, b) => b.calls - a.calls);
  }, [filtered]);

  if (!user?.isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={range}
          onChange={(e, v) => { if (v) setRange(v); }}
        >
          {RANGE_OPTIONS.map(r => <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">
          {totalCalls.toLocaleString()} calls{truncated ? ' (truncated)' : ''}{windowLabel ? ` · ${windowLabel}` : ''}
        </Typography>
      </Box>

      {range === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            type="datetime-local"
            label="From"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="datetime-local"
            label="To"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" color="text.secondary">
            From is required; leave To empty for “up to now”.
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Provider summary cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        {providerSummary.map(p => (
          <Card key={p.provider} variant="outlined" sx={{ minWidth: 140 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>{p.provider}</Typography>
              <Typography variant="h6">{p.calls.toLocaleString()}</Typography>
              <Typography variant="caption" color={p.errors ? 'error.main' : 'text.secondary'}>
                {p.errors} errors ({p.calls ? Math.round((p.errors / p.calls) * 100) : 0}%)
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerFilter} label="Provider" onChange={(e) => setProviderFilter(e.target.value)}>
            <MenuItem value="all">All providers</MenuItem>
            {providers.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>User</InputLabel>
          <Select value={userFilter} label="User" onChange={(e) => setUserFilter(e.target.value)}>
            <MenuItem value="">All users</MenuItem>
            {users.map(u => <MenuItem key={u} value={u}>{u === '-' ? '(unattributed)' : u}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Endpoint contains"
          value={endpointFilter}
          onChange={(e) => setEndpointFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Errors</TableCell>
                <TableCell align="right">Err %</TableCell>
                <TableCell align="right">Avg</TableCell>
                <TableCell align="right">P50</TableCell>
                <TableCell align="right">P95</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No API metrics recorded in the selected window.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r, i) => (
                <TableRow key={`${r.provider}|${r.endpoint}|${r.userId || '-'}|${i}`} hover>
                  <TableCell>
                    <Chip size="small" label={r.provider} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.endpoint}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.userId || '—'}</TableCell>
                  <TableCell align="right">{r.calls.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: r.errors ? 'error.main' : 'inherit' }}>{r.errors}</TableCell>
                  <TableCell align="right">{r.errorRate}%</TableCell>
                  <TableCell align="right">{fmtMs(r.avgLatencyMs)}</TableCell>
                  <TableCell align="right">{fmtMs(r.p50Ms)}</TableCell>
                  <TableCell align="right">{fmtMs(r.p95Ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminApiMetrics;
